/**
 * Dashboard ticker — headline figures scrolling left to right.
 *
 * Every entry comes from a table the platform actually populates. A metric
 * with no data is omitted server-side rather than rendered as a dash: a
 * ticker is read at a glance and believed without checking, which makes it
 * the worst place in the product for a plausible-looking placeholder.
 *
 * Gold was requested and is absent on purpose — nothing here collects a gold
 * price (MPPI-00058). A stale constant would be indistinguishable from a live
 * rate to anyone reading the strip.
 */
import { useTicker, type TickerItem } from "@/api/dashboard";

/** Older than this and the figure is marked rather than quietly shown. */
const STALE_DAYS = 3;

function isStale(asOf: string | null): boolean {
  if (!asOf) return true;
  const days = (Date.now() - new Date(asOf).getTime()) / 86_400_000;
  return days > STALE_DAYS;
}

function Item({ item }: { item: TickerItem }) {
  const stale = isStale(item.as_of);
  return (
    <span className="inline-flex items-baseline gap-1.5 px-4">
      <span className="text-[11px] uppercase tracking-wide text-gray-400">
        {item.label}
      </span>
      <span
        className={`text-xs font-semibold tabular-nums ${
          stale ? "text-gray-400" : "text-gray-900"
        }`}
        title={
          stale
            ? `Last updated ${item.as_of ?? "unknown"} — this source updates on a lag`
            : `As of ${item.as_of}`
        }
      >
        {item.display}
        {stale && <span className="ml-1 text-[10px] text-amber-600">·lag</span>}
      </span>
    </span>
  );
}

export function MetricTicker() {
  const { data, isLoading, isError } = useTicker();

  if (isLoading || isError || !data?.length) return null;

  return (
    <div
      className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white py-2"
      role="marquee"
      aria-label="Key market metrics"
    >
      {/* Two identical runs butted together, translated by exactly -50%, so
          the loop is seamless with no gap or jump at the wrap point. */}
      <div className="ticker-track flex w-max group-hover:[animation-play-state:paused]">
        {[0, 1].map((copy) => (
          <div key={copy} className="flex shrink-0" aria-hidden={copy === 1}>
            {data.map((item) => (
              <Item key={`${copy}-${item.key}`} item={item} />
            ))}
          </div>
        ))}
      </div>

      {/* Fade the edges so items enter and leave rather than being clipped. */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-white to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white to-transparent" />

      <style>{`
        @keyframes mppi-ticker { from { transform: translateX(0); }
                                 to   { transform: translateX(-50%); } }
        .ticker-track { animation: mppi-ticker 45s linear infinite; }
        /* Respect a reduced-motion preference: the figures still read, they
           just stop moving. */
        @media (prefers-reduced-motion: reduce) {
          .ticker-track { animation: none; flex-wrap: wrap; width: 100%; }
        }
      `}</style>
    </div>
  );
}
